/**
 * Chat content-block taxonomy — the first-class typed shape every
 * assistant message (and streamed delta) flows through.
 *
 * This replaces the legacy `ChatMessage.content: string` model that
 * destructively flattened richer block types (tool_use, thinking,
 * tool_result, image, ...) into plain text on the client. See
 * `ai-docs/lexiun-chat-content-model-plan.md` §1 + §2.
 *
 * The server-side chat.history RPC already returns assistant messages
 * with a block-array `content` field (see
 * `lexruntime/openclaw/src/gateway/chat-display-projection.ts`). The
 * SPA's job here is to normalize the various on-the-wire shapes the
 * upstream LLM providers use (anthropic, openai-codex, pi-ai) into one
 * canonical `ContentBlock` union, drive the renderer dispatcher off
 * `type` + (for tool_use) `name`, and stop throwing data away.
 *
 * Importantly, this file is the SINGLE source of truth for:
 *   - the union (extend here when a new block type ships)
 *   - the normalizers (extend here when a new wire-shape appears)
 *   - the per-tool plan-shape mappers consumed by `<TodoCard>`
 *
 * Adding a new block type: extend the union + add a normalizer branch
 * + add a `<BlockRenderer>` case (in `components/chat/BlockRenderer.tsx`).
 * That is the entire contract.
 */

import type { ChatAttachment } from "@/types/gateway";

// ── ContentBlock union — every type the renderer dispatcher knows ──

export interface TextBlock {
  type: "text";
  text: string;
}

export interface ThinkingBlock {
  type: "thinking";
  text: string;
  signature?: string;
}

/** Tool invocation — normalized from anthropic's `tool_use`,
 *  pi-native `toolCall`, openai's `function_call`, and the gateway's
 *  rekeyed `toolName`/`toolInput` variants. */
export interface ToolUseBlock {
  type: "tool_use";
  id: string;
  name: string;
  input: unknown;
}

/** Tool execution result — paired with a ToolUseBlock by `tool_use_id`. */
export interface ToolResultBlock {
  type: "tool_result";
  tool_use_id: string;
  content: string;
  status?: "ok" | "failed";
}

/** Image block — accepts anthropic `{source:{type:"base64", media_type, data}}`,
 *  openai `{image_url:{url}}`, and openclaw `{mediaId, mediaUrl}` shapes.
 *  Normalized to a single canonical shape; renderer picks `mediaUrl`
 *  first, then constructs a data-URL from `mediaId` + mediaType. */
export interface ImageBlock {
  type: "image";
  mediaUrl?: string;
  mediaId?: string;
  mediaType?: string;
  alt?: string;
}

/** SPA-originated attachment block — re-uses the existing
 *  ChatAttachment union so the outbound + inbound paths stay aligned. */
export interface AttachmentBlock {
  type: "attachment";
  attachment: ChatAttachment;
}

/** Document reference — surfaces an inline pointer to an AIEditor
 *  document with optional line-range + preview. SPA-originated. */
export interface DocumentRefBlock {
  type: "document_ref";
  docId: string;
  versionHash?: string;
  startLine?: number;
  endLine?: number;
  preview?: string;
}

export type ContentBlock =
  | TextBlock
  | ThinkingBlock
  | ToolUseBlock
  | ToolResultBlock
  | ImageBlock
  | AttachmentBlock
  | DocumentRefBlock;

// ── Type guards ──

export function isText(b: ContentBlock): b is TextBlock {
  return b.type === "text";
}
export function isThinking(b: ContentBlock): b is ThinkingBlock {
  return b.type === "thinking";
}
export function isToolUse(b: ContentBlock): b is ToolUseBlock {
  return b.type === "tool_use";
}
export function isToolResult(b: ContentBlock): b is ToolResultBlock {
  return b.type === "tool_result";
}
export function isImage(b: ContentBlock): b is ImageBlock {
  return b.type === "image";
}
export function isAttachment(b: ContentBlock): b is AttachmentBlock {
  return b.type === "attachment";
}
export function isDocumentRef(b: ContentBlock): b is DocumentRefBlock {
  return b.type === "document_ref";
}

// ── Plan-shape: TodoItem + mappers (consumed by <TodoCard>) ──

export interface TodoItem {
  /** Imperative form ("Review the draft") — shown when pending/completed. */
  content: string;
  /** Present-participle form ("Reviewing the draft") — shown when in_progress. */
  activeForm: string;
  status: "pending" | "in_progress" | "completed";
}

/** OpenClaw's native `update_plan` tool's `input.plan` → TodoItem[]. */
export function mapPlanToTodos(plan: unknown): TodoItem[] {
  if (!Array.isArray(plan)) return [];
  const out: TodoItem[] = [];
  for (const raw of plan) {
    if (!raw || typeof raw !== "object") continue;
    const step = (raw as { step?: unknown }).step;
    const activeForm = (raw as { activeForm?: unknown }).activeForm;
    const status = (raw as { status?: unknown }).status;
    const content = typeof step === "string" ? step : null;
    const af = typeof activeForm === "string" ? activeForm : content;
    if (
      content &&
      af &&
      (status === "pending" || status === "in_progress" || status === "completed")
    ) {
      out.push({ content, activeForm: af, status });
    }
  }
  return out;
}

/** Custom `lx_todo` tool's `input.todos` → TodoItem[]. */
export function mapLxTodos(todos: unknown): TodoItem[] {
  if (!Array.isArray(todos)) return [];
  const out: TodoItem[] = [];
  for (const raw of todos) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    if (
      typeof r.content === "string" &&
      typeof r.activeForm === "string" &&
      (r.status === "pending" ||
        r.status === "in_progress" ||
        r.status === "completed")
    ) {
      out.push({
        content: r.content,
        activeForm: r.activeForm,
        status: r.status,
      });
    }
  }
  return out;
}

/** Combined: dispatch by tool name to the right plan-mapper. Used by
 *  `<ToolUseBlock>` when rendering update_plan/lx_todo as TodoCard. */
export function todosFromToolUse(block: ToolUseBlock): TodoItem[] {
  const input = (block.input ?? {}) as Record<string, unknown>;
  if (block.name === "update_plan") return mapPlanToTodos(input.plan);
  if (block.name === "lx_todo") return mapLxTodos(input.todos);
  return [];
}

// ── Wire-shape normalizers — the one bottleneck for "stop throwing data away" ──

/** Map an arbitrary on-the-wire content block (from chat.history OR a
 *  streaming chat event) into our canonical `ContentBlock`. Returns
 *  null when the shape is unrecognized — the caller logs a console.warn
 *  for visibility (NEVER silent drop; that's how data-loss got us into
 *  this redesign in the first place). */
export function normalizeBlock(raw: unknown): ContentBlock | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const type = r.type;

  if (type === "text") {
    const text = typeof r.text === "string" ? r.text : "";
    if (!text) return null;
    return { type: "text", text };
  }

  if (type === "thinking") {
    const text = typeof r.text === "string" ? r.text : typeof r.thinking === "string" ? r.thinking : "";
    const signature = typeof r.signature === "string" ? r.signature : undefined;
    if (!text && !signature) return null;
    return { type: "thinking", text, signature };
  }

  // tool_use — anthropic / openclaw / openai-codex / pi-native variants
  // converge here. Observed shapes (all reconciled):
  //   Anthropic:        {type:"tool_use",  id, name, input}
  //   pi-native:        {type:"toolCall",  id, name, args}     (also `toolName`/`toolCallId`)
  //   gateway-rekeyed:  {type:"tool_use",  id, toolName, toolInput}
  //   openai-codex:     {type:"toolCall",  id, name, arguments}  ← this one
  //                     bit us during initial smoke: `arguments` wasn't
  //                     in the field list and update_plan inputs dropped.
  if (type === "tool_use" || type === "toolCall" || type === "tool_call" || type === "toolUse") {
    const id =
      typeof r.id === "string"
        ? r.id
        : typeof r.toolCallId === "string"
          ? r.toolCallId
          : "";
    const name =
      typeof r.name === "string"
        ? r.name
        : typeof r.toolName === "string"
          ? r.toolName
          : "";
    const input =
      r.input !== undefined
        ? r.input
        : r.toolInput !== undefined
          ? r.toolInput
          : r.args !== undefined
            ? r.args
            : r.arguments !== undefined
              ? r.arguments
              : {};
    if (!id || !name) return null;
    return { type: "tool_use", id, name, input };
  }

  // tool_result — anthropic / pi-native variants.
  if (type === "tool_result" || type === "toolResult") {
    const toolUseId =
      typeof r.tool_use_id === "string"
        ? r.tool_use_id
        : typeof r.toolUseId === "string"
          ? r.toolUseId
          : typeof r.toolCallId === "string"
            ? r.toolCallId
            : "";
    // Content can be a string OR an array-of-text-blocks (anthropic shape).
    let content = "";
    if (typeof r.content === "string") {
      content = r.content;
    } else if (Array.isArray(r.content)) {
      content = (r.content as Array<{ type?: string; text?: string }>)
        .filter((c) => c && c.type === "text" && typeof c.text === "string")
        .map((c) => c.text!)
        .join("");
    }
    const status: ToolResultBlock["status"] =
      r.status === "failed" || r.status === "ok"
        ? (r.status as "ok" | "failed")
        : r.is_error === true
          ? "failed"
          : undefined;
    if (!toolUseId) return null;
    return { type: "tool_result", tool_use_id: toolUseId, content, status };
  }

  // image — three variants:
  //   anthropic: {type:"image", source:{type:"base64", media_type, data}}
  //   openai:    {type:"image_url", image_url:{url}}
  //   openclaw:  {type:"image", mediaUrl|mediaId, mediaType, alt}
  if (type === "image" || type === "image_url") {
    let mediaUrl: string | undefined;
    let mediaType: string | undefined;
    const alt = typeof r.alt === "string" ? r.alt : undefined;
    const source = r.source as { type?: string; media_type?: string; data?: string } | undefined;
    const imageUrl = r.image_url as { url?: string } | undefined;
    if (typeof r.mediaUrl === "string") mediaUrl = r.mediaUrl;
    if (typeof r.mediaType === "string") mediaType = r.mediaType;
    if (source?.type === "base64" && source.media_type && source.data) {
      mediaUrl = `data:${source.media_type};base64,${source.data}`;
      mediaType = source.media_type;
    }
    if (typeof imageUrl?.url === "string") {
      mediaUrl = imageUrl.url;
    }
    const mediaId = typeof r.mediaId === "string" ? r.mediaId : undefined;
    if (!mediaUrl && !mediaId) return null;
    return { type: "image", mediaUrl, mediaId, mediaType, alt };
  }

  // attachment — SPA-originated; not expected to arrive from the wire
  // today (the outbound model still uses RPC-level attachments), but
  // the shape is recognized for future bidirectional parity.
  if (type === "attachment") {
    const attachment = r.attachment as ChatAttachment | undefined;
    if (!attachment) return null;
    return { type: "attachment", attachment };
  }

  // document_ref — SPA-originated. Same note as attachment.
  if (type === "document_ref") {
    const docId = typeof r.docId === "string" ? r.docId : "";
    if (!docId) return null;
    return {
      type: "document_ref",
      docId,
      versionHash: typeof r.versionHash === "string" ? r.versionHash : undefined,
      startLine: typeof r.startLine === "number" ? r.startLine : undefined,
      endLine: typeof r.endLine === "number" ? r.endLine : undefined,
      preview: typeof r.preview === "string" ? r.preview : undefined,
    };
  }

  // Audio — explicitly out-of-scope for v1 per the plan. Drop with a
  // visible warn so future audio content surfaces as "we received this
  // but don't render it yet" rather than silently disappearing.
  // Log keys only (no full payload — audio blocks can carry MBs).
  if (type === "audio") {
    // eslint-disable-next-line no-console
    console.warn("[content-blocks] audio block dropped (v1 unsupported)", { keys: Object.keys(r) });
    return null;
  }

  // Unknown type — log identifier + key list (NOT full payload, which
  // could be massive for images/binary data).
  // eslint-disable-next-line no-console
  console.warn("[content-blocks] unrecognized block type — dropped:", { type, keys: Object.keys(r) });
  return null;
}

/** Normalize an entire message's `content` field — string OR block
 *  array — into our canonical `ContentBlock[]`.
 *
 *  In the rich-content-model post-§0 world, `raw.content` SHOULD always
 *  be an array of blocks. If a string slips through (e.g. a wire-shape
 *  regression, a third-party tool returning unexpected shape), we log
 *  a warn + wrap as a single TextBlock. This is a DEFENSIVE fallback,
 *  not a migration intent.
 *
 *  Warn is logged at most ONCE per page session (chat.history typically
 *  returns dozens-to-hundreds of messages; with legacy chats pre-§0-wipe,
 *  a per-message warn would flood the console). */
let _warnedStringContent = false;
export function normalizeMessageContent(raw: unknown): ContentBlock[] {
  if (typeof raw === "string") {
    if (!_warnedStringContent) {
      _warnedStringContent = true;
      // eslint-disable-next-line no-console
      console.warn(
        "[content-blocks] string content received where block array expected — wrapping as TextBlock. Investigate the source. (logged once per session)",
      );
    }
    return raw ? [{ type: "text", text: raw }] : [];
  }
  if (!Array.isArray(raw)) return [];
  const out: ContentBlock[] = [];
  for (const block of raw) {
    const normalized = normalizeBlock(block);
    if (normalized) out.push(normalized);
  }
  return out;
}
